import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AppStore } from '../app-store/app-store.service';
import { ApiOperations } from 'src/app/models';
import { HttpClient } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';

// Interfaces para mejorar el tipado
interface Reserva {
  idOficina: number;
  dia: string;
  hora: string;
  oficina?: string;
  serie?: string;
  idSerie: number;
  rut: string;
  anulado?: number;
}

@Injectable({
  providedIn: 'root',
})
export class DataTableService {
  private apiOperations: ApiOperations | undefined;
  private reservasSubject = new BehaviorSubject<Reserva[]>([]);
  reservas$ = this.reservasSubject.asObservable();

  // Datos de ejemplo (se podrían mover a un archivo separado)
  private readonly datosEjemplo: Reserva[] = [
    {
      idOficina: 1,
      dia: '20/05/24',
      hora: '11:45 Horas',
      oficina: 'Av. Apoquindo 2702, Las Condes',
      idSerie: 15,
      serie: 'Detalle submotivo de visita',
      rut: '12345678-9',
    },
    {
      idOficina: 2,
      dia: '20/05/24',
      hora: '11:45 Horas',
      oficina: 'Av. Apoquindo 2702, Las Condes',
      idSerie: 16,
      serie: 'Detalle submotivo de visita',
      rut: '98765432-1',
    },
  ];

  constructor(private appStore: AppStore, private http: HttpClient) {
    const { operations } = this.appStore.env?.api ?? {};
    this.apiOperations = operations;
  }

  updateReservas(reservas: Reserva[]): void {
    this.reservasSubject.next(reservas);
  }

  getDataUpdate(): Reserva[] {
    return this.reservasSubject.getValue();
  }

  getItemById(id: number): Observable<Reserva | undefined> {
    // Usar los datos de ejemplo si no hay datos en el subject
    const reservas = this.reservasSubject.getValue();
    const todasReservas = reservas.length > 0 ? reservas : this.datosEjemplo;

    const item = todasReservas.find((d) => d.idOficina === id);
    return of(item);
  }

  findReservaByUniqueIdentifiers(
    idOficina: number,
    idSerie: number,
    dia: string,
    hora: string,
    rut: string
  ): number {
    const reservas = this.reservasSubject.getValue();
    return reservas.findIndex(
      (item) =>
        item.idOficina === idOficina &&
        item.idSerie === idSerie &&
        item.dia === dia &&
        item.hora === hora &&
        item.rut === rut
    );
  }

  async deleteData(reserva: Reserva): Promise<void> {
    const { idOficina, idSerie, dia, hora, rut } = reserva;

    const reservas = this.reservasSubject.getValue();
    // Usar el nuevo método para encontrar la reserva exacta
    const index = this.findReservaByUniqueIdentifiers(
      idOficina,
      idSerie,
      dia,
      hora,
      rut
    );

    if (index !== -1) {
      const item = reservas[index];

      try {
        this.anularReserva(item).subscribe({
          next: () => {
            // Actualizar el estado local
            const nuevasReservas = [...reservas];
            nuevasReservas.splice(index, 1);
            this.updateReservas(nuevasReservas);
          },
          error: (error) => {
            console.error('Error detallado al anular reserva:', error);
            return throwError(() => error);
          },
        });
      } catch (err) {
        console.error('Error inesperado en deleteData:', err);
      }
    } else {
      console.warn('No se encontró la reserva especificada');
    }
  }

  private anularReserva(reserva: Reserva): Observable<any> {
    const { idOficina, idSerie, dia, hora, rut } = reserva;

    // Verificar cada campo por separado para identificar cuál falta
    if (!idOficina) {
      console.warn('Campo faltante: idOficina');
    }
    if (!idSerie) {
      console.warn('Campo faltante: idSerie');
    }
    if (!dia) {
      console.warn('Campo faltante: dia');
    }
    if (!hora) {
      console.warn('Campo faltante: hora');
    }
    if (!rut) {
      console.warn('Campo faltante: rut');
    }

    if (!idOficina || !idSerie || !dia || !hora || !rut) {
      return throwError(
        () => new Error('Datos incompletos para anular la reserva')
      );
    }

    return this.postAnular(idOficina, idSerie, dia, hora, rut);
  }

  postAnular(
    idOficina: number,
    idSerie: number,
    dia: string,
    hora: string,
    rut: string
  ): Observable<any> {
    if (!this.apiOperations) {
      console.error('API operations no configuradas');
      return throwError(() => new Error('API operations no configuradas'));
    }

    if (!this.apiOperations.deleteSchedule) {
      console.error('Operation deleteSchedule no definida');
      return throwError(
        () => new Error('Operation deleteSchedule no definida')
      );
    }

    const body = { idOficina, idSerie, dia, hora, rut };

    const url = this.appStore.getUrlByOperation(
      this.apiOperations.deleteSchedule
    );

    return this.http.post<any>(url, body).pipe(
      tap((response) => response),
      catchError((error) => {
        console.error('Error en anulación - Status:', error.status);
        console.error('Error en anulación - Mensaje:', error.message || error);
        return throwError(() => error);
      })
    );
  }
}
